import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './CommercialCommissions.css';

const emptyForm = {
  commercial: '',
  puissance: '',
  typeCommission: 'montant_fixe',
  montant: '',
  pourcentage: ''
};

const getInitialFilters = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    commercialId: params.get('commercialId') || '',
    puissance: '',
    search: '',
    page: 1,
    limit: 10
  };
};

const CommercialCommissions = ({ onNavigate, currentPage }) => {
  const [commissions, setCommissions] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [puissances, setPuissances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [filters, setFilters] = useState(getInitialFilters);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const API_URL = import.meta.env.VITE_API_URL;
  const getApiUrl = (endpoint) => {
    const baseUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
    return `${baseUrl}${endpoint}`;
  };
  const getToken = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const selectedCommercialName = useMemo(() => {
    const commercial = commerciaux.find(c => c._id === filters.commercialId);
    return commercial ? `${commercial.prenom || ''} ${commercial.nom || ''}`.trim() : 'Tous les commerciaux';
  }, [commerciaux, filters.commercialId]);

  useEffect(() => {
    fetchCommerciaux();
  }, []);

  useEffect(() => {
    fetchCommissions();
  }, [filters.page, filters.limit]);

  useEffect(() => {
    if (filters.commercialId) {
      fetchPuissances(filters.commercialId);
    } else {
      setPuissances([]);
    }
  }, [filters.commercialId]);

  const fetchCommerciaux = async () => {
    try {
      const response = await axios.get(getApiUrl('/commerciaux'), { headers: authHeaders() });
      setCommerciaux(response.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des commerciaux');
    }
  };

  const fetchPuissances = async (commercialId) => {
    try {
      const response = await axios.get(getApiUrl(`/commercial-commissions/puissances?commercialId=${commercialId}`), {
        headers: authHeaders()
      });
      setPuissances(response.data.data || []);
    } catch (err) {
      setPuissances([]);
    }
  };

  const fetchCommissions = async (customFilters = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (customFilters.commercialId) params.set('commercialId', customFilters.commercialId);
      if (customFilters.puissance) params.set('puissance', customFilters.puissance);
      if (customFilters.search) params.set('search', customFilters.search);
      params.set('page', customFilters.page || 1);
      params.set('limit', customFilters.limit || 10);

      const response = await axios.get(getApiUrl(`/commercial-commissions?${params.toString()}`), {
        headers: authHeaders()
      });
      setCommissions(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des commissions');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (e) => {
    e.preventDefault();
    const next = { ...filters, page: 1 };
    setFilters(next);
    fetchCommissions(next);
  };

  const resetFilters = () => {
    const next = { commercialId: '', puissance: '', search: '', page: 1, limit: 10 };
    setFilters(next);
    fetchCommissions(next);
  };

  const openCreateModal = () => {
    setEditing(null);
    setFormData(emptyForm);
    setPuissances([]);
    setShowModal(true);
  };

  const openEditModal = async (commission) => {
    setEditing(commission);
    setFormData({
      commercial: commission.commercial?._id || commission.commercial,
      puissance: commission.puissance,
      typeCommission: commission.typeCommission,
      montant: commission.montant || '',
      pourcentage: commission.pourcentage || ''
    });
    await fetchPuissances(commission.commercial?._id || commission.commercial);
    setShowModal(true);
  };

  const handleFormChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'commercial') {
      setFormData(prev => ({ ...prev, commercial: value, puissance: '' }));
      if (value) await fetchPuissances(value);
      else setPuissances([]);
    }
  };

  const validateForm = () => {
    if (!formData.commercial) return 'Selectionnez un commercial';
    if (!formData.puissance || Number(formData.puissance) <= 0) return 'Selectionnez une puissance valide';
    if (formData.typeCommission === 'montant_fixe' && (!formData.montant || Number(formData.montant) <= 0)) {
      return 'Le montant fixe doit etre superieur a 0';
    }
    if (formData.typeCommission === 'pourcentage' && (!formData.pourcentage || Number(formData.pourcentage) <= 0 || Number(formData.pourcentage) > 100)) {
      return 'Le pourcentage doit etre compris entre 0 et 100';
    }
    return null;
  };

  const saveCommission = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      commercial: formData.commercial,
      puissance: Number(formData.puissance),
      typeCommission: formData.typeCommission,
      montant: formData.typeCommission === 'montant_fixe' ? Number(formData.montant) : null,
      pourcentage: formData.typeCommission === 'pourcentage' ? Number(formData.pourcentage) : null
    };

    try {
      setSaving(true);
      if (editing) {
        await axios.put(getApiUrl(`/commercial-commissions/${editing._id}`), payload, { headers: authHeaders() });
        setMessage('Commission modifiee avec succes');
      } else {
        await axios.post(getApiUrl('/commercial-commissions'), payload, { headers: authHeaders() });
        setMessage('Commission ajoutee avec succes');
      }
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm);
      await fetchCommissions();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const deleteCommission = async (commission) => {
    const result = await Swal.fire({
      title: 'Supprimer cette commission ?',
      text: 'Cette configuration ne sera plus disponible pour le mode automatique.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b'
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(getApiUrl(`/commercial-commissions/${commission._id}`), { headers: authHeaders() });
      setMessage('Commission supprimee avec succes');
      await fetchCommissions();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);
  const formatDateTime = (value) => value ? new Date(value).toLocaleString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="ccf-layout">
      <BackOfficeMenu onNavigate={onNavigate} currentPage={currentPage} />
      <div className="ccf-content">
        <div className="ccf-top">
          <div>
            <div className="ccf-eyebrow">Administration</div>
            <h1>Configuration des commissions</h1>
            <p>Grilles personnalisables par commercial et par puissance.</p>
          </div>
          <button className="ccf-primary" onClick={openCreateModal}>
            <i className="fas fa-plus"></i> Ajouter
          </button>
        </div>

        {message && <div className="ccf-success"><i className="fas fa-check-circle"></i> {message}</div>}
        {error && <div className="ccf-error"><i className="fas fa-exclamation-triangle"></i> {error}</div>}

        <form className="ccf-filters" onSubmit={applyFilters}>
          <div className="ccf-filter-field wide">
            <label>Recherche</label>
            <input
              value={filters.search}
              onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Commercial, puissance, montant..."
            />
          </div>
          <div className="ccf-filter-field">
            <label>Commercial</label>
            <select
              value={filters.commercialId}
              onChange={e => setFilters(prev => ({ ...prev, commercialId: e.target.value, puissance: '' }))}
            >
              <option value="">Tous</option>
              {commerciaux.map(commercial => (
                <option key={commercial._id} value={commercial._id}>{commercial.prenom} {commercial.nom}</option>
              ))}
            </select>
          </div>
          <div className="ccf-filter-field">
            <label>Puissance</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.puissance}
              onChange={e => setFilters(prev => ({ ...prev, puissance: e.target.value }))}
              placeholder="kWc"
            />
          </div>
          <div className="ccf-filter-actions">
            <button type="submit" className="ccf-secondary"><i className="fas fa-filter"></i> Filtrer</button>
            <button type="button" className="ccf-ghost" onClick={resetFilters}>Reinitialiser</button>
          </div>
        </form>

        <div className="ccf-summary">
          <div>
            <span>Vue active</span>
            <strong>{selectedCommercialName}</strong>
          </div>
          <div>
            <span>Total</span>
            <strong>{pagination.total}</strong>
          </div>
          <div>
            <span>Page</span>
            <strong>{pagination.page}/{pagination.totalPages}</strong>
          </div>
        </div>

        <div className="ccf-table-card">
          {loading ? (
            <LoadingSpinner message="Chargement des commissions..." />
          ) : (
            <table className="ccf-table">
              <thead>
                <tr>
                  <th>Commercial</th>
                  <th>Puissance</th>
                  <th>Type</th>
                  <th>Montant</th>
                  <th>Pourcentage</th>
                  <th>Date creation</th>
                  <th>Derniere modification</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr><td colSpan="8" className="ccf-empty">Aucune configuration trouvee.</td></tr>
                ) : commissions.map(commission => (
                  <tr key={commission._id}>
                    <td>
                      <div className="ccf-commercial-name">{commission.commercial?.prenom} {commission.commercial?.nom}</div>
                      <span>{commission.commercial?.mail || '-'}</span>
                    </td>
                    <td><strong>{commission.puissance} kWc</strong></td>
                    <td>
                      <span className={`ccf-badge ${commission.typeCommission === 'montant_fixe' ? 'fixed' : 'percent'}`}>
                        {commission.typeCommission === 'montant_fixe' ? 'Montant fixe' : 'Pourcentage'}
                      </span>
                    </td>
                    <td>{commission.typeCommission === 'montant_fixe' ? formatCurrency(commission.montant) : '-'}</td>
                    <td>{commission.typeCommission === 'pourcentage' ? `${commission.pourcentage}%` : '-'}</td>
                    <td>{formatDateTime(commission.createdAt)}</td>
                    <td>{formatDateTime(commission.updatedAt)}</td>
                    <td>
                      <div className="ccf-actions">
                        <button onClick={() => openEditModal(commission)} title="Modifier"><i className="fas fa-edit"></i></button>
                        <button className="danger" onClick={() => deleteCommission(commission)} title="Supprimer"><i className="fas fa-trash"></i></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="ccf-pagination">
          <button
            className="ccf-ghost"
            disabled={pagination.page <= 1}
            onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
          >
            <i className="fas fa-chevron-left"></i> Precedent
          </button>
          <span>{pagination.total} configuration(s)</span>
          <button
            className="ccf-ghost"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Suivant <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      {showModal && (
        <div className="ccf-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ccf-modal" onClick={e => e.stopPropagation()}>
            <div className="ccf-modal-header">
              <h2>{editing ? 'Modifier la commission' : 'Ajouter une commission'}</h2>
              <button onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form className="ccf-form" onSubmit={saveCommission}>
              <div className="ccf-form-grid">
                <div className="ccf-form-field full">
                  <label>Commercial *</label>
                  <select name="commercial" value={formData.commercial} onChange={handleFormChange} required>
                    <option value="">Selectionner</option>
                    {commerciaux.map(commercial => (
                      <option key={commercial._id} value={commercial._id}>{commercial.prenom} {commercial.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="ccf-form-field">
                  <label>Puissance existante *</label>
                  <select name="puissance" value={formData.puissance} onChange={handleFormChange} required disabled={!formData.commercial}>
                    <option value="">{formData.commercial ? 'Choisir une puissance' : 'Choisir un commercial'}</option>
                    {puissances.map(puissance => (
                      <option key={puissance} value={puissance}>{puissance} kWc</option>
                    ))}
                  </select>
                  {formData.commercial && puissances.length === 0 && (
                    <small>Aucune puissance deja affectee a ce commercial.</small>
                  )}
                </div>
                <div className="ccf-form-field">
                  <label>Type *</label>
                  <select name="typeCommission" value={formData.typeCommission} onChange={handleFormChange}>
                    <option value="montant_fixe">Montant fixe</option>
                    <option value="pourcentage">Pourcentage</option>
                  </select>
                </div>
                {formData.typeCommission === 'montant_fixe' ? (
                  <div className="ccf-form-field full">
                    <label>Montant (TND) *</label>
                    <input name="montant" type="number" min="0.001" step="0.001" value={formData.montant} onChange={handleFormChange} placeholder="Ex: 1500" />
                  </div>
                ) : (
                  <div className="ccf-form-field full">
                    <label>Pourcentage (%) *</label>
                    <input name="pourcentage" type="number" min="0.01" max="100" step="0.01" value={formData.pourcentage} onChange={handleFormChange} placeholder="Ex: 5" />
                  </div>
                )}
              </div>
              <div className="ccf-modal-actions">
                <button type="button" className="ccf-ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="ccf-primary" disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommercialCommissions;
